import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Settings, CheckCircle2, XCircle, Loader2, Save,
  Eye, EyeOff, Trash2, TestTube, Cloud, Server, ChevronDown,
  Link, Unlink, Building2, RefreshCw, ExternalLink
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_ICONS = {
  fatture_in_cloud: Cloud,
  openapi_it: Server,
};

// ─── Pannello OAuth Fatture in Cloud ─────────────────────────────────────────
const FICOAuthPanel = ({ getAuthHeaders, onDisconnected }) => {
  const [status, setStatus]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [authUrl, setAuthUrl]       = useState("");
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting]       = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied]         = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/sdi/fatture-in-cloud/oauth/status`, {
        headers: getAuthHeaders(),
      });
      setStatus(res.data);
      return res.data;
    } catch {
      setStatus({ connected: false });
      return { connected: false };
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getAuthHeaders]);

  // Polling automatico dopo apertura link OAuth — dichiarato PRIMA di loadAuthUrl
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      const s = await fetchStatus(true);
      if (s?.connected) {
        clearInterval(pollRef.current);
        toast.success("Fatture in Cloud connesso!");
        setStatus(s);
      }
      if (attempts >= 40) clearInterval(pollRef.current); // timeout 2 min (40 × 3s)
    }, 3000);
  }, [fetchStatus]);

  const loadAuthUrl = useCallback(async () => {
    setLoadingUrl(true);
    try {
      const res = await axios.get(`${API}/sdi/fatture-in-cloud/oauth/start`, {
        headers: getAuthHeaders(),
        params: { origin_url: window.location.origin },
      });
      setAuthUrl(res.data.auth_url);
      startPolling(); // avvia polling indipendentemente da come viene aperto il link
      return res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore generazione link");
      return null;
    } finally {
      setLoadingUrl(false);
    }
  }, [getAuthHeaders, startPolling]);

  useEffect(() => {
    fetchStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("fic_connected") === "1") {
      toast.success("Fatture in Cloud connesso con successo!");
      window.history.replaceState({}, "", window.location.pathname);
      fetchStatus();
    }
    if (params.get("fic_error") === "1") {
      toast.error("Autorizzazione Fatture in Cloud fallita. Riprova.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  const handleOpenOAuth = async () => {
    const url = authUrl || await loadAuthUrl();
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
    startPolling();
    toast.info("Completa l'autorizzazione nella nuova scheda, poi torna qui.", { duration: 6000 });
  };

  const handleCopy = async () => {
    const url = authUrl || await loadAuthUrl();
    if (!url) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback per browser che non supportano clipboard API
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copia non riuscita — seleziona il testo manualmente");
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await axios.delete(`${API}/sdi/fatture-in-cloud/oauth/disconnect`, {
        headers: getAuthHeaders(),
      });
      toast.success("Fatture in Cloud disconnesso");
      setStatus({ connected: false });
      setTestResult(null);
      setAuthUrl("");
      onDisconnected?.();
    } catch {
      toast.error("Errore durante la disconnessione");
    }
    setDisconnecting(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/sdi/config/test`, {}, { headers: getAuthHeaders() });
      setTestResult(res.data);
      if (res.data.connected) toast.success(res.data.message || "Connessione OK");
      else toast.error(res.data.message || "Connessione fallita");
    } catch {
      setTestResult({ connected: false, message: "Errore durante il test" });
    }
    setTesting(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="fic-oauth-panel">

      {/* Stato connessione */}
      {status?.connected ? (
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800">Connesso</p>
            {status.company_name && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Building2 className="w-3 h-3" />{status.company_name}
              </p>
            )}
          </div>
          <Badge className="bg-green-100 text-green-700 text-[10px] flex-shrink-0">OAuth2</Badge>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
          <XCircle className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <p className="text-sm text-slate-600">Non connesso</p>
        </div>
      )}

      {/* Sezione connessione / riconnessione */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {status?.connected ? "Aggiorna connessione" : "Connetti account"}
        </p>

        {/* Istruzioni */}
        <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs text-slate-600 space-y-1">
          <p>1. Clicca <strong>"Apri autorizzazione"</strong> — si apre Fatture in Cloud in una nuova scheda</p>
          <p>2. Effettua il login e autorizza l'accesso</p>
          <p>3. Torna qui — la connessione si aggiorna in automatico</p>
          <p className="text-slate-400">Se il popup è bloccato, usa il link manuale qui sotto.</p>
        </div>

        {/* Pulsante principale */}
        <Button
          onClick={handleOpenOAuth}
          disabled={loadingUrl}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          data-testid="connect-fic-btn"
        >
          {loadingUrl
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Caricamento...</>
            : <><Link className="w-4 h-4 mr-2" />{status?.connected ? "Riconnetti / Aggiorna" : "Apri autorizzazione"}</>
          }
        </Button>

        {/* Link manuale copiabile */}
        <div className="space-y-1.5">
          {!authUrl && (
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs text-slate-500"
              onClick={loadAuthUrl}
              disabled={loadingUrl}
              data-testid="load-fic-url-btn"
            >
              {loadingUrl ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Mostra link manuale
            </Button>
          )}
          {authUrl && (
            <div className="space-y-1">
              <p className="text-[10px] text-slate-400 font-medium">Link autorizzazione — clicca per selezionare, poi Ctrl+C</p>
              <div className="flex gap-1.5">
                <input
                  readOnly
                  value={authUrl}
                  onClick={e => e.target.select()}
                  className="flex-1 min-w-0 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-[10px] text-slate-600 font-mono cursor-text focus:outline-none focus:border-blue-400"
                  data-testid="fic-auth-url-input"
                />
                <a
                  href={authUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 flex-shrink-0"
                  data-testid="open-fic-url-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Test + Disconnetti */}
      {status?.connected && (
        <div className="space-y-2 pt-1 border-t border-slate-100">
          {testResult && (
            <div className={`flex items-center gap-2 p-2.5 rounded-lg text-xs ${
              testResult.connected
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`} data-testid="test-result">
              {testResult.connected
                ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                : <XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
              }
              {testResult.message}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="flex-1 text-xs" data-testid="test-fic-btn">
              {testing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <TestTube className="w-3.5 h-3.5 mr-1" />}
              Testa
            </Button>
            <Button variant="outline" size="sm" onClick={() => fetchStatus()} className="text-xs" data-testid="refresh-fic-status-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnecting} className="text-red-600 border-red-200 hover:bg-red-50 text-xs" data-testid="disconnect-fic-btn">
              {disconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const ConfigurazioneFatture = ({ embedded = false }) => {
  const { getAuthHeaders } = useAuth();

  const [providers, setProviders] = useState([]);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Form state
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [showPasswords, setShowPasswords] = useState({});

  // Dialog conferma eliminazione
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [providersRes, configRes] = await Promise.all([
        axios.get(`${API}/sdi/providers`),
        axios.get(`${API}/sdi/config`, { headers: getAuthHeaders() }),
      ]);
      setProviders(providersRes.data.providers || []);
      const cfg = configRes.data;
      setCurrentConfig(cfg);

      if (cfg.configured) {
        setSelectedProvider(cfg.provider);
        setCredentials(cfg.credentials || {});
      }
    } catch (err) {
      console.error("Errore caricamento configurazione:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectProvider = (providerKey) => {
    if (selectedProvider === providerKey) return;
    setSelectedProvider(providerKey);
    setCredentials({});
    setTestResult(null);
  };

  const handleCredentialChange = (key, value) => {
    setCredentials((prev) => ({ ...prev, [key]: value }));
    setTestResult(null);
  };

  const handleTogglePassword = (key) => {
    setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (!selectedProvider) {
      toast.error("Seleziona un provider");
      return;
    }

    const providerInfo = providers.find((p) => p.key === selectedProvider);
    const requiredFields = (providerInfo?.credentials || []).filter(
      (c) => c.type !== "checkbox"
    );
    for (const field of requiredFields) {
      if (!credentials[field.key]?.trim()) {
        toast.error(`Il campo "${field.label}" è obbligatorio`);
        return;
      }
    }

    try {
      setSaving(true);
      await axios.post(
        `${API}/sdi/config`,
        {
          provider: selectedProvider,
          credentials,
          enabled: true,
        },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      toast.success("Configurazione salvata");
      await fetchData();
    } catch (err) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    // Salva prima, poi testa
    if (!currentConfig?.configured) {
      await handleSave();
    }
    try {
      setTesting(true);
      setTestResult(null);
      const res = await axios.post(`${API}/sdi/config/test`, {}, {
        headers: getAuthHeaders(),
      });
      setTestResult(res.data);
      if (res.data.connected) {
        toast.success(res.data.message || "Connessione riuscita!");
      } else {
        toast.error(res.data.message || "Connessione fallita");
      }
    } catch (err) {
      setTestResult({ connected: false, message: "Errore durante il test" });
      toast.error("Errore durante il test di connessione");
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/sdi/config`, { headers: getAuthHeaders() });
      toast.success("Configurazione rimossa");
      setSelectedProvider(null);
      setCredentials({});
      setTestResult(null);
      setDeleteDialogOpen(false);
      await fetchData();
    } catch (err) {
      toast.error("Errore nella rimozione");
    }
  };

  const selectedProviderInfo = providers.find((p) => p.key === selectedProvider);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? "min-h-[20vh]" : "min-h-[60vh]"}`}>
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${embedded ? "" : "max-w-2xl mx-auto"}`} data-testid="configurazione-fatture-page">
      {/* Header - nascosto in modalità embedded */}
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-indigo-500" />
            Configurazione Import Fatture
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Scegli il provider e inserisci le credenziali per importare le fatture elettroniche
          </p>
        </div>
      )}

      {/* Selezione Provider */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700">Scegli il provider</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {providers.map((provider) => {
            const Icon = PROVIDER_ICONS[provider.key] || Cloud;
            const isSelected = selectedProvider === provider.key;
            const isCurrent = currentConfig?.provider === provider.key && currentConfig?.configured;

            return (
              <Card
                key={provider.key}
                data-testid={`provider-card-${provider.key}`}
                className={`cursor-pointer transition-all ${
                  isSelected
                    ? "ring-2 ring-indigo-500 border-indigo-300 bg-indigo-50/50"
                    : "hover:border-gray-300 bg-white"
                }`}
                onClick={() => handleSelectProvider(provider.key)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-indigo-100" : "bg-gray-100"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 ${isSelected ? "text-indigo-600" : "text-gray-500"}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {provider.label}
                        </h3>
                        {isCurrent && (
                          <Badge className="bg-green-100 text-green-700 text-[10px]">
                            Attivo
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                        {provider.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Form Credenziali / OAuth Panel */}
      {selectedProviderInfo && (
        <Card className="bg-white" data-testid="credentials-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ChevronDown className="w-4 h-4 text-gray-400" />
              {selectedProvider === "fatture_in_cloud" ? "Connessione Fatture in Cloud" : `Credenziali ${selectedProviderInfo.label}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* FIC usa OAuth2 — niente form manuale */}
            {selectedProvider === "fatture_in_cloud" ? (
              <FICOAuthPanel
                getAuthHeaders={getAuthHeaders}
                onDisconnected={fetchData}
              />
            ) : (
              <>
                {selectedProviderInfo.credentials.map((field) => {
                  if (field.type === "checkbox") {
                    return (
                      <div key={field.key} className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">{field.label}</Label>
                          {field.help && (
                            <p className="text-xs text-gray-500 mt-0.5">{field.help}</p>
                          )}
                        </div>
                        <Switch
                          data-testid={`credential-${field.key}`}
                          checked={!!credentials[field.key]}
                          onCheckedChange={(val) => handleCredentialChange(field.key, val)}
                        />
                      </div>
                    );
                  }

                  const isPassword = field.type === "password";
                  return (
                    <div key={field.key} className="space-y-1.5">
                      <Label className="text-sm font-medium">{field.label}</Label>
                      <div className="relative">
                        <Input
                          data-testid={`credential-${field.key}`}
                          type={isPassword && !showPasswords[field.key] ? "password" : "text"}
                          value={credentials[field.key] || ""}
                          onChange={(e) => handleCredentialChange(field.key, e.target.value)}
                          placeholder={field.help}
                          className="pr-10"
                        />
                        {isPassword && (
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => handleTogglePassword(field.key)}
                          >
                            {showPasswords[field.key] ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                      {field.help && (
                        <p className="text-xs text-gray-400">{field.help}</p>
                      )}
                    </div>
                  );
                })}

                {/* Test Result */}
                {testResult && (
                  <div
                    data-testid="test-result"
                    className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                      testResult.connected
                        ? "bg-green-50 text-green-800 border border-green-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                    }`}
                  >
                    {testResult.connected ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    )}
                    <span>{testResult.message}</span>
                  </div>
                )}

                {/* Azioni */}
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    data-testid="save-config-btn"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-indigo-500 hover:bg-indigo-600 flex-1"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Salva
                  </Button>
                  <Button
                    data-testid="test-connection-btn"
                    variant="outline"
                    onClick={handleTest}
                    disabled={testing}
                    className="flex-1"
                  >
                    {testing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4 mr-2" />
                    )}
                    Testa Connessione
                  </Button>
                  {currentConfig?.configured && (
                    <Button
                      data-testid="delete-config-btn"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog conferma eliminazione */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rimuovi configurazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler rimuovere la configurazione del provider? Non potrai
              importare le fatture finché non ne configurerai uno nuovo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
            >
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigurazioneFatture;
